import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/shared/Modal';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Icon } from '../../components/shared/Icons';
import { deleteClip } from '../../services/clips.service';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';
import { invalidateClipCaches } from '../../hooks/use-clips';

interface BulkDeleteClipsDialogProps {
  open: boolean;
  onClose: () => void;
  // Snapshot taken at the moment the user clicked Delete in the selection bar. We
  // intentionally don't re-derive from live selection state — if the user changed the
  // selection while the dialog was open, the dialog still operates on what they confirmed.
  clips: { id: string; title: string }[];
  // Reports which IDs succeeded vs failed so the page can drop the succeeded ones from
  // selection while keeping the failed ones (so the user can retry).
  onDone?: (result: { succeededIds: string[]; failedIds: string[] }) => void;
}

// Bulk delete confirm dialog. Fan-out via Promise.allSettled — no backend bulk endpoint
// needed, and each per-clip call rides the existing federation scope + audit + S3 cleanup.
// Partial failures are reported in the toast and via onDone (so the caller can keep the
// failed clips selected for a retry).
export function BulkDeleteClipsDialog({ open, onClose, clips, onDone }: BulkDeleteClipsDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);

  // Cap the visible title list — when the user picks 30 clips the modal shouldn't become
  // a scroll wall. 5 + "and X more" matches Linear/Notion-style bulk-action dialogs.
  const HEAD = 5;
  const head = clips.slice(0, HEAD);
  const restCount = Math.max(0, clips.length - HEAD);

  async function onConfirm() {
    setRunning(true);
    try {
      // allSettled (not all) so a single 409 / network error doesn't abort the rest of
      // the fan-out. We want "succeeded what we could, failed loudly on the rest".
      const results = await Promise.allSettled(clips.map((c) => deleteClip(c.id)));
      const succeededIds: string[] = [];
      const failedIds: string[] = [];
      let firstError: unknown = null;
      results.forEach((r, i) => {
        const id = clips[i]?.id;
        if (!id) return;
        if (r.status === 'fulfilled') {
          succeededIds.push(id);
          // Drop the single-clip cache for each deleted clip so a stale tab on the editor
          // route doesn't keep rendering the dead clip from cache. The list invalidation
          // below covers the list view.
          qc.removeQueries({ queryKey: ['clip', id] });
        } else {
          failedIds.push(id);
          firstError ??= r.reason;
        }
      });

      // One sweep at the end — invalidating per-id N times would refetch every list N times with
      // no benefit, and stutter the UI. Sweeps EVERY clip cache (admin table, referee library,
      // Home hero), not just the admin list: deleting here must not leave the clip on the shelf.
      invalidateClipCaches(qc);

      if (failedIds.length === 0) {
        notifySuccess(t('admin.clips.bulkDeleteDialog.successToast', { count: succeededIds.length }));
      } else if (succeededIds.length === 0) {
        // All failed — surface the first error verbatim so the operator sees the real cause
        // (most likely a CLIP_PUBLISHED race or a network blip), with a count fallback.
        notifyError(firstError, t('admin.clips.bulkDeleteDialog.allFailedToast', { count: failedIds.length }));
      } else {
        notifyWarning(
          t('admin.clips.bulkDeleteDialog.partialToast', {
            succeeded: succeededIds.length,
            failed: failedIds.length,
            reason: apiErrorMessage(firstError, 'see logs'),
          }),
        );
      }

      onDone?.({ succeededIds, failedIds });
      onClose();
    } finally {
      setRunning(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={running ? () => {} : onClose}
      title={t('admin.clips.bulkDeleteDialog.title', { count: clips.length })}
      sub={clips.length === 1 ? clips[0]?.title : t('admin.clips.bulkDeleteDialog.subSelected', { count: clips.length })}
      panelClass="max-w-md"
      footer={
        <>
          <Button onClick={onClose} disabled={running}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={running || clips.length === 0}>
            {running ? <Spinner className="w-3.5 h-3.5" /> : <Icon.trash className="w-3.5 h-3.5" />}
            {t('admin.clips.bulkDeleteDialog.deleteButton', { count: clips.length })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] text-ink-2">
        <p>
          {t('admin.clips.bulkDeleteDialog.bodyMain')}
          <span className="font-semibold text-ink"> {t('admin.clips.bulkDeleteDialog.bodyEmphasis')}</span>
        </p>
        <div className="rounded-sm border border-line-2 bg-bg-2 p-3 text-[12.5px]">
          <ul className="flex flex-col gap-1 text-ink-2">
            {head.map((c) => (
              <li key={c.id} className="truncate">
                <span className="font-mono text-muted">{c.id.slice(0, 8)}…</span> · {c.title}
              </li>
            ))}
          </ul>
          {restCount > 0 && (
            <p className="mt-1.5 text-muted">{t('admin.clips.bulkDeleteDialog.andMore', { count: restCount })}</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
